import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import products from './products';
import './App.css';

function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = products.find(p => p.id === id);
  const [latestVersion, setLatestVersion] = useState(null);
  const [debugInfo, setDebugInfo] = useState({prodId: '', lines: [], comparisons: []});
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  
  useEffect(() => {
    if (!product) return;
    fetch('/versions.txt?t=' + Date.now())
      .then(res => res.ok ? res.text() : Promise.reject('Could not fetch version file'))
      .then(text => {
        const lines = text.split(/\r?\n/).filter(Boolean);
        const prodId = (product.id || '').toLowerCase().trim();
        let found = null;
        let comparisons = [];
        for (const line of lines) {
          const [key, val] = line.split(':');
          const keyTrim = (key || '').toLowerCase().trim();
          comparisons.push(`'${prodId}' vs '${keyTrim}'`);
          if (keyTrim === prodId) {
            found = val ? val.trim() : '';
            break;
          }
        }
        setDebugInfo({prodId, lines, comparisons});
        if (found) {
          setLatestVersion(found);
        } else {
          setLatestVersion(null);
        }
      })
      .catch(() => setLatestVersion(null));
  }, [product]);

  const [mainIdx, setMainIdx] = useState(0);
  const [sliderStart, setSliderStart] = useState(0);
  const sliderVisibleCount = 4;

  const total = product ? product.screenshots.length : 0;
  const canSlideLeft = mainIdx > 0;
  const canSlideRight = mainIdx < total - 1;
  const mainImage = product && product.screenshots[mainIdx];

  // Move slider window so selector stays at the end of the visible range when possible
  useEffect(() => {
    if (!product) return;
    if (mainIdx < sliderVisibleCount) {
      setSliderStart(0);
    } else if (mainIdx > total - 1) {
      setSliderStart(total - sliderVisibleCount);
    } else if (mainIdx >= sliderStart + sliderVisibleCount) {
      setSliderStart(mainIdx - sliderVisibleCount + 1);
    } else if (mainIdx < sliderStart) {
      setSliderStart(mainIdx);
    }
  }, [mainIdx, total, sliderStart, product]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isImageModalOpen) {
        setIsImageModalOpen(false);
      }
    };

    if (isImageModalOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isImageModalOpen]);

  const handleThumbClick = idx => setMainIdx(idx);

  const handleSliderLeft = () => {
    if (canSlideLeft) {
      setMainIdx(mainIdx - 1);
    }
  };
  
  const handleSliderRight = () => {
    if (canSlideRight) {
      setMainIdx(mainIdx + 1);
    }
  };

  const handleMainImageClick = () => {
    setIsImageModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsImageModalOpen(false);
  };

  if (!product) return <div className="App"><h2>Product not found</h2></div>;

  // Custom content for NeuLogic Date Calendar
  const defaultDescriptions = {
    'date-calendar': {
      short: 'A comprehensive year-view calendar for quick date reference, holiday tracking, and date calculations.',
      full: (
        <>
          <p><b>NeuLogic Date Calendar</b> provides a clean, efficient interface for viewing an entire year at once. Ideal for those who need a straightforward date reference tool without the complexity of scheduling software.</p>
          <ul>
            <li>Full year calendar with customizable holiday tracking</li>
            <li>Multiple date calculation utilities</li>
            <li>Date series generation for recurring events</li>
            <li>Support for various date input formats</li>
          </ul>
          <p>Whether planning vacations, tracking holidays, or calculating important dates, this tool offers an intuitive solution that works entirely offline with no unnecessary features.</p>
          <b>Key Features:</b>
          <ul>
            <li>Complete Year View — See all months simultaneously for efficient planning</li>
            <li>Holiday Management — Track standard holidays and add custom dates</li>
            <li>Advanced Date Calculations — Add/subtract days, find days between dates, generate date series</li>
            <li>Visual Highlighting — Easily identify weekends, holidays, and today's date</li>
            <li>Simple Year Navigation — Quick access to past and future years</li>
            <li>Offline Security — Works without internet connection for complete privacy</li>
            <li>Clean Interface — Distraction-free design focused on functionality</li>
            <li>Automatic Settings Retention — Preferences stored locally for convenience</li>
          </ul>
          <b>Ideal For:</b>
          <ul>
            <li>Professionals needing quick date reference and planning tools</li>
            <li>Anyone tracking holidays and important dates</li>
            <li>Users who prefer simplicity and efficiency over complex interfaces</li>
            <li>Those concerned with privacy and offline functionality</li>
          </ul>
          <p>NeuLogic Date Calendar is designed as a reference and calculation tool, not a scheduling system—providing exactly what you need without unnecessary complications.</p>
          <b>Screenshots:</b>
          <ul>
            <li>Full-year calendar view with highlighted holidays and weekends</li>
            <li>The Holiday Editor showing customization options</li>
            <li>The Day Calculator performing date calculations</li>
            <li>The Settings panel allowing users to configure preferences</li>
          </ul>
          <b>App Features:</b>
          <ul>
            <li>Simple, full-year calendar view for quick date reference</li>
            <li>Track public holidays and add custom ones</li>
            <li>Built-in day calculator to determine past or future dates</li>
            <li>Minimalist interface without unnecessary complexity</li>
            <li>No internet connection required</li>
            <li>Automatic settings save for convenience</li>
          </ul>
          <b>System Requirements:</b>
          <ul>
            <li>Windows 10 or higher</li>
            <li>70 MB of free disk space</li>
            <li>512 MB RAM</li>
          </ul>
        </>
      )
    }
  };

  // Use custom description if product is Date Calendar
  const desc = product.id === 'datecalendar' && defaultDescriptions['date-calendar'] ? defaultDescriptions['date-calendar'] : { short: product.shortDesc, full: product.description };

  return (
    <>
      <div className="productpage-flex productpage-flex-darktext">
        <div className="productpage-mainimg-col">
          <div className="productpage-mainimg-frame productpage-mainimg-frame-nobg">
            <img
              src={mainImage}
              alt={product.name + ' screenshot'}
              className="productpage-mainimg"
              onClick={handleMainImageClick}
              style={{ cursor: 'pointer' }}
            />
          </div>
          <div className="productpage-slider-row productpage-slider-row-tighter">
            <button
              className="slider-arrow"
              onClick={handleSliderLeft}
              disabled={!canSlideLeft}
              aria-label="Previous images"
              type="button"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="11" stroke="#1670b2" strokeWidth="2" fill="#181c22" />
                <polyline points="15,7 9,12 15,17" fill="none" stroke="#1670b2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="slider-thumbs">
              {product.screenshots.slice(sliderStart, sliderStart + sliderVisibleCount).map((img, idx) => (
                <img
                  key={sliderStart + idx}
                  src={img}
                  alt={product.name + ' thumb'}
                  className={mainIdx === sliderStart + idx ? 'slider-thumb selected' : 'slider-thumb'}
                  onClick={() => handleThumbClick(sliderStart + idx)}
                  style={{ cursor: 'pointer' }}
                />
              ))}
            </div>
            <button
              className="slider-arrow"
              onClick={handleSliderRight}
              disabled={!canSlideRight}
              aria-label="Next images"
              type="button"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="11" stroke="#1670b2" strokeWidth="2" fill="#181c22" />
                <polyline points="9,7 15,12 9,17" fill="none" stroke="#1670b2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
        <div className="productpage-details-col productpage-details-dark">
          <h1 className="productpage-title productpage-title-white" style={{marginBottom: '8px'}}>{product.name}</h1>
          {latestVersion && (
            <div className="productpage-version" style={{marginTop: 0, marginBottom: '24px'}}>
              Latest Version: <span>{latestVersion}</span>
            </div>
          )}
          <div className="productpage-desc productpage-desc-white" dangerouslySetInnerHTML={{ __html: product.description }} />
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center', margin: '32px 0 8px 0' }}>
            <span style={{ fontWeight: 'bold', fontSize: '1.3em', textAlign: 'center' }}>
              {product.price === 0 ? 'Price: Free' : `Price: $${product.price.toFixed(2)}`}
            </span>
          </div>
          <div className="productpage-actions productpage-actions-row">
            <button className="card-btn productpage-btn-small" onClick={() => navigate('/')} type="button" style={{cursor:'pointer'}}>&larr; Home</button>
            <a href={product.download} className="card-btn productpage-btn-small" download style={{cursor:'pointer'}}>Download</a>
            {product.price === 0 ? (
              <button
                className="card-btn productpage-btn-small"
                type="button"
                style={{cursor:'not-allowed', opacity:0.65, position:'relative'}}
                disabled
                title="This app is a gift to you."
              >
                Purchase
              </button>
            ) : (
              <a
                href={product.stripeLink}
                className="card-btn productpage-btn-small"
                target="_blank"
                rel="noopener noreferrer"
                style={{cursor:'pointer'}}
              >
                Purchase
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {isImageModalOpen && (
        <div 
          className="image-modal-overlay"
          onClick={handleCloseModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
        >
          <div 
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              maxWidth: '95vw',
              maxHeight: '95vh',
              gap: '24px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={mainImage}
              alt={product.name + ' full size'}
              style={{
                maxWidth: '100%',
                maxHeight: 'calc(95vh - 80px)',
                objectFit: 'contain',
                border: '2px solid #fff',
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
                cursor: 'pointer'
              }}
              onClick={(e) => {
                e.stopPropagation();
                setMainIdx(mainIdx > 0 ? mainIdx - 1 : total - 1);
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setMainIdx(mainIdx < total - 1 ? mainIdx + 1 : 0);
              }}
              onMouseEnter={(e) => {
                const tooltip = document.querySelector('.custom-tooltip');
                if (tooltip) tooltip.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                const tooltip = document.querySelector('.custom-tooltip');
                if (tooltip) tooltip.style.opacity = '0';
              }}
            />
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <button
                onClick={handleCloseModal}
                className="card-btn productpage-btn-small"
                type="button"
                style={{cursor: 'pointer'}}
              >
                Close
              </button>
              <div
                className="custom-tooltip"
                style={{
                  position: 'absolute',
                  top: '50%',
                  right: '100%',
                  transform: 'translateY(-50%)',
                  marginRight: '20px',
                  backgroundColor: '#ffeb3b',
                  color: '#333',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  fontSize: '14px',
                  whiteSpace: 'pre-line',
                  textAlign: 'center',
                  opacity: '0',
                  transition: 'opacity 0.2s',
                  pointerEvents: 'none',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                  zIndex: 10001,
                  width: '200px'
                }}
              >
                Left-click for previous image{'\n'}Right-click for next image
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ProductPage;